import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  MuiLink,
} from 'components/mui';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CUSTOMERS, ROUTE_CONTACTS } from 'constants/routerPath';
import { Case } from 'containers/pages/cases/types';

interface CaseGeneralInfoProps {
  caseData: Case;
  onReferenceClick?: (referenceId: number) => void;
}

function fixRelativeImagePaths(html: string): string {
  return html.replace(
    /src="(?!https?:\/\/|\/)(.*?)"/gi,
    'src="/Portal/$1"',
  );
}

function DescriptionSection({ description }: { description: string }) {
  if (!description) {
    return (
      <Typography variant="body2" color="text.secondary">
        No description available
      </Typography>
    );
  }

  return (
    <Box
      sx={{ '& p': { m: 0 }, '& img': { maxWidth: '100%' }, typography: 'body2' }}
      dangerouslySetInnerHTML={{ __html: fixRelativeImagePaths(description) }}
    />
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
  );
}

interface InfoRowProps {
  label: string;
  value: string | number;
  onClick?: () => void;
}

function InfoRow({ label, value, onClick }: InfoRowProps) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {onClick && value ? (
        <MuiLink
          component="button"
          variant="body2"
          onClick={onClick}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {value}
        </MuiLink>
      ) : (
        <Typography variant="body2">{value || 'N/A'}</Typography>
      )}
    </Box>
  );
}

function formatDate(dateStr: string) {
  if (!dateStr) return 'N/A';
  try {
    const dotNetMatch = /\/Date\((-?\d+)\)\//.exec(dateStr);
    const d = dotNetMatch
      ? new Date(parseInt(dotNetMatch[1], 10))
      : new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return dateStr; }
}

function CaseGeneralInfo({ caseData, onReferenceClick }: CaseGeneralInfoProps) {
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {/* Description */}
      <Paper sx={{ p: 2 }}>
        <SectionHeader title="Description" />
        <DescriptionSection description={caseData.Description} />
      </Paper>

      {/* Contacts & GPD */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <SectionHeader title="Contacts" />
            <InfoRow
              label="Customer"
              value={caseData.CustomerName}
              onClick={caseData.CustomerId ? () => navigate(`${ROUTE_CUSTOMERS}/${caseData.CustomerId}`) : undefined}
            />
            <InfoRow
              label="Consultant"
              value={caseData.ConsultantName}
              onClick={caseData.ConsultantContactId ? () => navigate(`${ROUTE_CONTACTS}/${caseData.ConsultantContactId}`) : undefined}
            />
            <InfoRow
              label="Primary Contact"
              value={caseData.PrimaryContactName}
              onClick={caseData.PrimaryContactId ? () => navigate(`${ROUTE_CONTACTS}/${caseData.PrimaryContactId}`) : undefined}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <SectionHeader title="Group / Project / Deliverable" />
            <InfoRow label="Group" value={caseData.GroupTitle} />
            <InfoRow label="Project" value={caseData.ProjectTitle} />
            <InfoRow label="Deliverable" value={caseData.DeliverableTitle} />
          </Paper>
        </Grid>
      </Grid>

      {/* Activity */}
      <Paper sx={{ p: 2 }}>
        <SectionHeader title="Activity" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InfoRow label="Status" value={caseData.StatusTitle} />
            <InfoRow label="Time Spent" value={caseData.TimeSpent || '0'} />
            <InfoRow label="# of Activities" value={caseData.NumberOfActivities} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoRow label="Created" value={formatDate(caseData.CreatedOn)} />
            <InfoRow label="Last Update" value={formatDate(caseData.UpdatedOn)} />
          </Grid>
        </Grid>
      </Paper>

      {/* Keywords & References */}
      <Paper sx={{ p: 2 }}>
        <SectionHeader title="Keywords" />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {caseData.Keywords?.length > 0 ? (
            caseData.Keywords.map((kw) => (
              <Chip key={kw.Id} label={kw.Title} size="small" />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No keywords
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: 1 }} />
        <SectionHeader title="References" />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {caseData.References?.length > 0 ? (
            caseData.References.map((ref) => (
              <Chip
                key={ref.Id}
                label={ref.Title}
                size="small"
                color="primary"
                variant="outlined"
                clickable
                onClick={() => onReferenceClick?.(ref.Id)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No references
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CaseGeneralInfo;
